// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize for lower per-GB pricing on both mobile ($2/GB flat) and residential ($1/GB flat) proxies with self-serve setup. Choose Oxylabs for much broader mobile coverage — 140+ countries versus Proxidize's five — a larger IP pool, or enterprise procurement with SLAs and a dedicated account manager.";

export const KEY_TAKEAWAYS = [
  "Proxidize undercuts Oxylabs on published pricing for both products. $200 vs $500 for 100 GB of mobile, for example.",
  "Oxylabs covers far more countries on mobile. 140+ countries versus Proxidize's five (US, Canada, UK, Germany, India).",
  "Oxylabs has a smaller entry-level mobile plan. 4 GB for $30, versus Proxidize's 25 GB / $50 minimum.",
  "Proxidize is fully self-serve; some Oxylabs plans route through sales. Free sign-up with no credit card versus sales-assisted trials on some Oxylabs plans.",
  "Oxylabs' residential pool is larger by IP count. 175M+ IPs versus Proxidize's ethically-sourced pool across 195+ countries.",
  "Oxylabs targets down to ZIP code and coordinates; Proxidize targets by city and ASN. Choose based on how granular your targeting needs to be.",
];

export interface VerdictCard {
  provider: "proxidize" | "oxylabs";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs across multiple countries at a flat $2/GB with city-level targeting",
      "Residential proxies in 195+ countries at $1/GB with rollover bandwidth",
      "Lower published per-GB rates than Oxylabs on both products at every plan size",
      "Per-proxy plans with unlimited bandwidth",
      "Self-serve setup: credentials in under a minute, no contract",
      "Sticky/random modes, manual rotation, and an API rotation URL",
      "Free sign-up without a credit card or sales call",
    ],
  },
  {
    provider: "oxylabs",
    title: "Choose Oxylabs if you need",
    points: [
      "Much broader mobile coverage — 140+ countries",
      "A very large rotating pool (20M+ mobile IPs, 175M+ residential IPs)",
      "A small-usage entry point: their 4 GB mobile plan is $30",
      "ZIP-code, coordinate, and state-level targeting granularity",
      "Enterprise procurement, SLAs, and an assigned account manager",
      "One vendor for scraping APIs and datasets beyond proxies",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  oxylabs: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Mobile IPs, self-serve simplicity",
    oxylabs: "Global mobile coverage, enterprise scale",
  },
  {
    feature: "Mobile network",
    proxidize: "50+ locations, major mobile networks",
    oxylabs: "20M+ IPs across 140+ countries",
  },
  {
    feature: "Mobile country coverage",
    proxidize: "US, Canada, UK, Germany, India",
    oxylabs: "140+ countries",
  },
  {
    feature: "Mobile targeting",
    proxidize: "City + ASN",
    oxylabs: "Continent, country, state, city, coordinates, ASN",
  },
  {
    feature: "Mobile pricing",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    oxylabs: "$3.50 to $7.50/GB by plan",
  },
  {
    feature: "100 GB mobile plan",
    proxidize: "$200/mo ($2.00/GB)",
    oxylabs: "$500/mo ($5.00/GB)",
  },
  {
    feature: "Smallest mobile entry point",
    proxidize: "25 GB for $50",
    oxylabs: "4 GB for $30",
  },
  {
    feature: "Pricing model",
    proxidize: "Per-GB or per-proxy, self-serve, no contract",
    oxylabs: "Fixed-GB plans with top-ups",
  },
  { feature: "Per-proxy mobile plans", proxidize: true, oxylabs: "Not publicly listed" },
  {
    feature: "Unlimited-bandwidth option",
    proxidize: "Yes, on per-proxy plans",
    oxylabs: "Not publicly listed",
  },
  {
    feature: "Sticky sessions",
    proxidize: "Sticky or random per access point",
    oxylabs: "24-hour static sessions",
  },
  {
    feature: "Rotation control",
    proxidize: "Custom interval (min 1 min), manual, API rotation URL",
    oxylabs: "Automatic rotation",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    oxylabs: "HTTP(S), HTTP3, SOCKS5",
  },
  { feature: "Free sign-up", proxidize: "No credit card required", oxylabs: "Via sales team" },
  { feature: "Self-serve signup", proxidize: true, oxylabs: "Sales-assisted for trials" },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    oxylabs: "175M+ IPs, 195+ countries",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    oxylabs: "$2.50 to $6/GB by plan",
  },
  {
    feature: "100 GB residential plan",
    proxidize: "$100/mo ($1.00/GB)",
    oxylabs: "$500/mo (Advanced, 125 GB)",
  },
  {
    feature: "Rollover of unused residential bandwidth",
    proxidize: "Yes, doesn't expire",
    oxylabs: "See their terms",
  },
  {
    feature: "Security certifications",
    proxidize: "SOC 2 Type II, ISO 27001",
    oxylabs: "See their trust center",
  },
  {
    feature: "Money-back guarantee",
    proxidize: "30 days (usage & policy limits)",
    oxylabs: "See their terms",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "Oxylabs" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Mobile scraping in Proxidize's covered countries",
    fit: "Proxidize",
    reason: "Premium pooled mobile IPs with city targeting at a lower per-GB rate.",
  },
  {
    useCase: "Global, multi-country mobile scraping",
    fit: "Oxylabs",
    reason: "140+ countries of mobile coverage vs Proxidize's smaller, growing footprint.",
  },
  {
    useCase: "Account workflows needing a stable exit",
    fit: "Proxidize",
    reason: "Per-proxy plans give you a consistent exit you control, with unlimited bandwidth.",
  },
  {
    useCase: "Price monitoring on US marketplaces",
    fit: "Proxidize",
    reason:
      "High-trust premium pooled mobile IPs on strict targets, sticky sessions for cart flows, per-GB billing.",
  },
  {
    useCase: "Global SERP / SEO rank tracking",
    fit: "Both",
    reason:
      "Proxidize covers it with residential proxies in 195+ countries; Oxylabs with global mobile or its SERP tooling.",
  },
  {
    useCase: "AI training data collection",
    fit: "Both",
    reason:
      "Residential is the default for broad crawls on either provider; use mobile for strict, mobile-first targets.",
  },
  {
    useCase: "Enterprise procurement with SLAs",
    fit: "Both",
    reason:
      "Oxylabs is built around enterprise sales; Proxidize offers enterprise plans with SOC 2 Type II and ISO 27001.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call.",
  },
  {
    title: "Pick per-GB or per-proxy",
    description:
      "Per-GB gives flexible access to the full pool billed by data. Per-proxy gives you real mobile IPs of your own with unlimited bandwidth.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a city or random rotation and set sticky or random IP behavior, the same knobs you tuned in the Oxylabs dashboard.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
